import React, { PropsWithChildren, useCallback } from 'react'
import { FieldErrors, FieldValues, SubmitHandler, UseFormReturn } from 'react-hook-form'
import cl from 'classnames'
import { observer } from 'mobx-react'

import { Button } from 'Shared/molecules/index'

import { NotificationProps } from 'Core/interfaces'

import styles from './styles.module.scss'

interface Props<T extends FieldValues> {
	form: UseFormReturn<T>
	errorMessage: string
	isSaving?: boolean
	onSave: SubmitHandler<T>
	className?: string
	isAutoMarginLeft?: boolean
	isDirty: boolean
	disabled?: boolean
	showNotification: (params: NotificationProps) => void
	getFailedValidationFields: (errors: FieldErrors) => string[]
}

const ButtonEditSave = <T extends object>(props: PropsWithChildren<Props<T>>) => {
	const {
		form,
		errorMessage,
		onSave,
		isSaving = false,
		className,
		isAutoMarginLeft = true,
		isDirty,
		disabled = false,
		showNotification,
		getFailedValidationFields
	} = props

	const { handleSubmit } = form

	const onFailure = useCallback(
		(errors) => {
			const failedValidationFields = errors ? getFailedValidationFields(errors) : []

			showNotification({
				msg: `${errorMessage} ${failedValidationFields.join(', ')}`,
				type: 'error'
			})
		},
		[errorMessage, getFailedValidationFields, showNotification]
	)

	const handleClick = useCallback(
		(e) => {
			handleSubmit(onSave, onFailure)(e)

			e.currentTarget.blur()
		},
		[handleSubmit, onSave, onFailure]
	)

	return (
		<Button
			className={cl(className, { [styles.autoMarginLeft]: isAutoMarginLeft })}
			iconType="dashboard"
			onClick={handleClick}
			disabled={!isDirty || disabled}
			loading={isSaving}
		>
			Save
		</Button>
	)
}

export default observer(ButtonEditSave)
